import { SubmitHandler, useForm } from "react-hook-form";
import { Mail } from "lucide-react";

import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@ui/card";
import LockIcon from "@mui/icons-material/Lock";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import FormTextInput from "@components/items/inputs/FormTextInput";
import { FieldType } from "@utils/types/components";
import { emailRegex } from "@utils/helpers/emailValidation";
import { signUp, useSignUp } from "@lib/stores";
import TranslatedText from "@components/items/display/TranslatedText";

type Input = {
  email: string;
  password: string;
};

interface SignUpProps {
  email: string;
  onCancel: () => void;
}

export const SignupByEmail = ({
  email = "example@gmail.com",
  onCancel,
}: SignUpProps) => {
  const { status } = useSignUp();

  const {
    handleSubmit,
    control,
    formState: { isDirty, errors },
  } = useForm<Input>({
    defaultValues: {
      email,
      password: "",
    },
    mode: "all",
  });

  const isDisabled =
    !isDirty || Object.keys(errors).length !== 0 || status === "loading";

  const onFormSubmit: SubmitHandler<Input> = (data) => {
    signUp(data);
  };

  return (
    <Card className="flex h-auto w-full flex-col items-center justify-center rounded-xl border-none bg-dark_background py-3 text-white sm:w-[434px]">
      <CardHeader>
        <CardTitle className="text-center text-[20px] font-medium">
          <TranslatedText
            text={`onboarding.titles.${"create an account using email"}`}
          />
        </CardTitle>
      </CardHeader>
      <form className="w-full">
        <CardContent className="mb-2 flex w-full flex-col items-center justify-center gap-3">
          <FormTextInput
            inputExtraClassNames="placeholder:rtl:pr-1 placeholder:ltr:pl-1 !bg-[#D9D9D9]"
            control={control}
            name={`email`}
            fieldMeta={{
              fieldType: FieldType.Text,
              placeHolder: "name@gmail.com",
              fieldOptions: { pattern: emailRegex() },
              required: true,
            }}
            required
            inputHtmlType="email"
            prefix={<Mail className="h-6 w-6 fill-primary text-[#D9D9D9]" />}
            validationErrorMessages={{
              pattern: "please enter a valid email",
            }}
            onEnter={() => {
              handleSubmit(onFormSubmit)();
            }}
            readOnly
          />
          <FormTextInput
            inputExtraClassNames="!bg-[#D9D9D9]"
            control={control}
            name={`password`}
            fieldMeta={{
              fieldType: FieldType.Text,
              fieldOptions: { minLength: 8 },
              required: true,
            }}
            inputHtmlType="password"
            prefix={<LockIcon className="text-primary" />}
            validationErrorMessages={{
              minLength: "The password must be at least 8 characters",
            }}
            onEnter={() => {
              handleSubmit(onFormSubmit)();
            }}
          />
        </CardContent>
        <CardFooter className="flex w-full items-center justify-center gap-5">
          <ShadcnButton
            className="bg-transparent hover:bg-transparent"
            onClick={onCancel}
            type="button"
          >
            <TranslatedText
              text={`onboarding.buttons.${"cancel"}`}
              className="text-[16px] font-normal leading-[22px] text-white"
            />
          </ShadcnButton>
          <ShadcnButton
            disabled={isDisabled}
            className="px-6 text-white"
            isLoading={status === "loading"}
            onClick={handleSubmit(onFormSubmit)}
          >
            <TranslatedText
              text={`onboarding.buttons.${"create an account"}`}
              className="text-[16px] font-normal leading-[22px]"
            />
          </ShadcnButton>
        </CardFooter>
      </form>
    </Card>
  );
};
